/**
 * Confirmación antes de pisar la entrada de un Messaging Service que manda las respuestas a OTRA
 * aplicación (A1-10). Antes se pisaba sin avisar y la otra app dejaba de recibir mensajes.
 *
 * El backend no escribe nada hasta que el operador confirma: la primera corrida vuelve con
 * `requiere_confirmacion` y `urlAnterior`, y «Cambiarlo a Deenex» reenvía con `reemplazarEntrada`.
 * Un túnel nuestro viejo NO llega acá: el backend lo reconoce como propio y lo reemplaza solo.
 */

import { useEffect, useId } from "react";
import { AlertTriangle, Loader2, X } from "lucide-react";
import { useFocusTrap } from "../../../../hooks/useFocusTrap";

export function ModalReemplazarEntrada({ urlAnterior, onConfirmar, onCancelar, busy }: {
  urlAnterior: string; onConfirmar: () => void; onCancelar: () => void; busy: boolean;
}) {
  const trapRef = useFocusTrap<HTMLDivElement>(true);
  const idTitulo = useId();
  const idDesc = useId();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape" && !busy) onCancelar(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [busy, onCancelar]);

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 animate-fade-in p-4">
      <div ref={trapRef} role="dialog" aria-modal="true" aria-labelledby={idTitulo} aria-describedby={idDesc}
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 id={idTitulo} className="font-semibold text-lg text-slate-900">¿Cambiar la entrada a Deenex?</h3>
          <button type="button" onClick={onCancelar} disabled={busy} aria-label="Cerrar"
            className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 disabled:opacity-50">
            <X size={18} />
          </button>
        </div>
        <div className="p-6 flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 bg-amber-50">
            <AlertTriangle size={22} className="text-amber-600" />
          </div>
          <p id={idDesc} className="text-slate-600 text-sm leading-relaxed pt-1">
            Este Messaging Service ya manda las respuestas a{" "}
            <span className="font-mono text-xs break-all text-slate-800">{urlAnterior || "otra dirección"}</span>.
            Si lo cambiás, esa otra aplicación deja de recibirlas. ¿Cambiarlo a Deenex?
          </p>
        </div>
        <div className="flex gap-3 px-6 py-4 bg-slate-50 border-t border-slate-100">
          <button type="button" onClick={onCancelar} disabled={busy}
            className="flex-1 py-2.5 rounded-xl text-sm font-semibold border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50">
            Dejarlo como está
          </button>
          <button type="button" onClick={onConfirmar} disabled={busy}
            className="flex-1 py-2.5 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 text-white bg-violet-600 hover:bg-violet-700 disabled:opacity-50">
            {busy && <Loader2 size={14} className="animate-spin" />} Cambiarlo a Deenex
          </button>
        </div>
      </div>
    </div>
  );
}

export default ModalReemplazarEntrada;
